import { ArrowRight, Lock, ShieldAlert } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/layout/Logo';
import { useAuth } from '@/auth/AuthProvider';
import { useAccess } from '@/features/AccessProvider';
import { useFirstAccessiblePath } from '@/features/nav';

/**
 * Forbidden.
 *
 * Three genuinely different situations used to share one message about the
 * signed-in user's role — including for visitors who weren't signed in at all,
 * since `/forbidden` is a public route. And the only action offered was sign
 * out, so a user who merely opened one page they lacked was stuck: hence the
 * "gets stuck in forbidden page" report.
 */
export function ForbiddenPage() {
  const { logout, user, ready, isAuthenticated } = useAuth();
  const access = useAccess();
  const location = useLocation();
  const firstAccessible = useFirstAccessiblePath();

  const deniedPath = (location.state as { from?: string } | null)?.from ?? null;
  const signedOut = ready && !isAuthenticated;
  const hasSomeAccess = access.hasAnyViewableResource;

  if (signedOut) {
    return (
      <Shell icon={<Lock className="h-10 w-10 text-muted-foreground" aria-hidden />} title="Sign in to continue">
        <p className="mt-2 text-sm text-muted-foreground">
          This page needs an authenticated Fasto account.
        </p>
        <Button className="mt-6" variant="primary" asChild>
          <Link to="/login">Go to sign in</Link>
        </Button>
      </Shell>
    );
  }

  // Signed in and holds grants, but hit a page outside them. This is a
  // wrong-turn, not a dead end — so lead with a way onward.
  if (hasSomeAccess) {
    return (
      <Shell icon={<Lock className="h-10 w-10 text-warning" aria-hidden />} title="You can't open this page">
        <p className="mt-2 text-sm text-muted-foreground">
          {deniedPath ? (
            <>
              <span className="font-mono text-xs text-foreground">{deniedPath}</span> isn&apos;t part of your access.
            </>
          ) : (
            <>That page isn&apos;t part of your access.</>
          )}{' '}
          Ask a Fasto admin to grant it if you need it.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {firstAccessible ? (
            <Button variant="primary" asChild>
              <Link to={firstAccessible}>
                Take me somewhere I can go
                <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden />
              </Link>
            </Button>
          ) : null}
          <Button variant="outline" asChild>
            <Link to="/settings">Settings</Link>
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell icon={<ShieldAlert className="h-10 w-10 text-warning" aria-hidden />} title="No access to Fasto BI">
      <p className="mt-2 text-sm text-muted-foreground">
        Your role <span className="font-medium capitalize">{user?.role ?? 'unknown'}</span> has no BI pages assigned
        yet. Reach out to the Fasto admin team if this looks wrong.
      </p>
      <Button className="mt-6" variant="outline" onClick={() => void logout()}>
        Sign in as a different user
      </Button>
    </Shell>
  );
}

function Shell({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid min-h-screen place-items-center px-6">
      <div className="flex w-full max-w-md flex-col items-center text-center">
        <Logo size={32} />
        <div className="mt-8">{icon}</div>
        <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">{title}</h1>
        {children}
      </div>
    </div>
  );
}
